import { FusionError, serializeFusionError } from "@fusion-advantage/backcompat"
import { err, ok, Result, ResultAsync } from "neverthrow"

// the majority case will be uploaded jpegs from the iOS camera,
// which don't have the heic efficiency (and those wouldn't render in our
// chromium based document render environment anyway)
// so it is worth compressing down to a smaller jpeg

export const compressPhoto = async (file: File): Promise<Result<File, FusionError>> => {
	const rImg = await ResultAsync.fromPromise(
		new Promise<HTMLImageElement>((resolve, reject) => {
			const img = new Image()
			img.onload = () => resolve(img)
			img.onerror = (ex) => reject(ex)
			img.src = URL.createObjectURL(file)
		}),
		ex => ex as unknown,
	)
	if (rImg.isErr()) {
		return err(serializeFusionError('compressPhoto', rImg.error))
	}
	const img = rImg.value
	const canvas = document.createElement('canvas')
	// reduce the size of the photo to maximum 1280px in either dimension
	// (or keep it the same size if both dimensions are below that)
	const maxDimension = 1280
	const scale = Math.min(1, maxDimension / Math.max(img.naturalWidth, img.naturalHeight))
	canvas.width = Math.round(img.naturalWidth * scale)
	canvas.height = Math.round(img.naturalHeight * scale)
	let ctx: CanvasRenderingContext2D | null
	try {
		ctx = canvas.getContext('2d')
		if (!ctx) {
			return err(serializeFusionError('compressPhoto', 'expected 2d ctx but null'))
		}
		ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
	} catch (ex) {
		return err(serializeFusionError('compressPhoto', ex))
	} finally {
		URL.revokeObjectURL(img.src)
	}
	// save as jpeg quality 70%
	// combined with dimension reductions, target size <300kb from camera
	const jpegQuality = 0.7
	const rBlob = await ResultAsync.fromPromise(
		new Promise<Blob>((resolve, reject) => {
			canvas.toBlob(
				blob => {
					if (blob) resolve(blob)
					else reject(new Error('expected result blob but null'))
				},
				'image/jpeg',
				jpegQuality,
			)
		}),
		ex => ex as unknown,
	)
	if (rBlob.isErr()) {
		return err(serializeFusionError('compressPhoto', rBlob.error))
	}
	return ok(new File(
		[rBlob.value],
		file.name.replace(/\.[^.]+$/, '.jpg'),
		{ type: 'image/jpeg' },
	))
}
